// VodInfoStore — IndexedDB local para detalhes enriquecidos de VOD.
// Permite SWR (stale-while-revalidate): 2ª abertura do mesmo filme renderiza
// IMEDIATO do cache local, e em paralelo dispara SSE pra atualizar.
//
// Schema versionado: bump SCHEMA_VERSION quando shape do VodItem mudar
// — abrir nova versão do DB limpa cache antigo.
//
// **Scope idêntico ao local-db**: chaves seguem `<providerPart>::<profilePart>::<vodId>`
// onde providerPart é `p:CODE` ou `d:LIST` (forma canônica de scope.ts).
// Antes (v2 inicial) usávamos providerKey raw (sem prefixo) — diferente
// do local-db. Padronizamos pra fonte única em scope.ts. Bump v3
// invalida caches antigos com a forma raw.

import type { VodItem } from '@app-iptv/iptv-client'
import type { DBSchema, IDBPDatabase } from 'idb'

import { makeDbOpener } from './idb-opener'
import { providerPart, profilePart } from './scope'

// v4 (2026-05-08): adiciona `lastReadAt` pra suportar LRU eviction
// no idb-quota-manager. Items v3 sem field tratados como
// `lastReadAt = storedAt` na primeira leitura.
export const VOD_INFO_SCHEMA_VERSION = 4

export interface VodInfoRecord {
  /** Chave composta: `${providerPart}::${profilePart}::${vodId}` */
  key: string
  /** `p:CODE` ou `d:LIST`. Mesma forma do local-db.providerScope. */
  providerScope: string
  /** `pf:<id>` ou `pf:_main`. Permite filtrar por perfil sem escanear keys. */
  profileScope: string
  vodId: string
  storedAt: number // unix ms
  /** Última leitura — usado pelo LRU eviction do idb-quota-manager.
   *  Atualizado fire-and-forget no get(); custo trivial. */
  lastReadAt: number
  schemaVersion: number
  /** VodItem enriquecido (pós-merge canonical+TMDB). */
  item: VodItem & { ext?: string | undefined }
}

interface Schema extends DBSchema {
  vodInfo: {
    key: string
    value: VodInfoRecord
    indexes: {
      'by-provider': string
      'by-profile': string
      'by-last-read': number // LRU index
    }
  }
}

const DB_NAME = 'iptv-vod-info'
// v3: chave passa a usar providerPart (`p:CODE`/`d:LIST`) — mesma forma
// do local-db. Records v2 (providerKey raw) ficam órfãos. Sem prod = OK.
// v4: adiciona índice by-last-read pra LRU eviction.
const DB_VERSION = 4

/**
 * Abertura via o molde unico (idb-opener.ts): solta a promise quando a
 * abertura falha, com guarda de identidade e janela de reabertura, e registra
 * o banco pro "Limpar dados". Antes, este modulo tinha o proprio `getDb`
 * com a promise da abertura guardada pra sempre: UMA rejeicao matava o cache
 * de fichas de filme pela sessao inteira, em silencio.
 *
 * Falha de abertura continua fluindo pro catch de cada metodo (→ rede).
 */
const vodInfoDb = makeDbOpener<Schema>({
  name: DB_NAME,
  version: DB_VERSION,
  upgrade(db, oldVersion, _newVersion, tx) {
    // v3 ou anterior: drop store inteiro (records v2 com providerKey
    // raw ficam órfãos — sem prod, OK).
    if (oldVersion < 3 && db.objectStoreNames.contains('vodInfo')) {
      db.deleteObjectStore('vodInfo')
    }
    if (!db.objectStoreNames.contains('vodInfo')) {
      const store = db.createObjectStore('vodInfo', { keyPath: 'key' })
      store.createIndex('by-provider', 'providerScope')
      store.createIndex('by-profile', 'profileScope')
      store.createIndex('by-last-read', 'lastReadAt')
    } else if (oldVersion < 4) {
      // v3 → v4: store mantida; adiciona índice by-last-read.
      // Records v3 sem `lastReadAt` ficam fora do índice (undefined
      // não indexa). evictLru trata via fallback no scan completo.
      const store = tx.objectStore('vodInfo')
      if (!store.indexNames.contains('by-last-read')) {
        store.createIndex('by-last-read', 'lastReadAt')
      }
    }
  },
})

function getDb(): Promise<IDBPDatabase<Schema>> {
  return vodInfoDb.get()
}

/** TTL: 7 dias. Posters/sinopse mudam raramente. SWR atualiza em background. */
export const VOD_INFO_TTL_MS = 7 * 24 * 60 * 60 * 1000

/** Debounce do touch lastReadAt (M2 do code review): só atualiza se
 *  passou >1min desde último touch. SWR pattern faz get 2× rápido
 *  (cache + revalidate); sem debounce eram 2 puts redundantes. Reduz
 *  100x as escritas e fecha janela de race com evictLru pra ~1min. */
const TOUCH_DEBOUNCE_MS = 60_000

/** Compõe key a partir do scope canônico (provider+profile do scope.ts).
 *  Retorna null se logged out — caller faz no-op. */
function composeKey(vodId: string): { key: string; providerScope: string; profileScope: string } | null {
  const provider = providerPart()
  if (!provider) return null
  const profile = profilePart()
  return {
    key: `${provider}::${profile}::${vodId}`,
    providerScope: provider,
    profileScope: profile,
  }
}

export const vodInfoStore = {
  /** Lê item do cache. Retorna null se ausente, shape incompatível, TTL
   *  expirado OU sem scope ativo (logged out).
   *
   *  Touch `lastReadAt` fire-and-forget — mantém LRU correto pro
   *  idb-quota-manager. Custo trivial (1 put assíncrono). */
  async get(vodId: string): Promise<VodInfoRecord | null> {
    const composed = composeKey(vodId)
    if (!composed) return null
    try {
      const db = await getDb()
      const record = await db.get('vodInfo', composed.key)
      if (!record) return null
      if (record.schemaVersion !== VOD_INFO_SCHEMA_VERSION) return null
      if (Date.now() - record.storedAt > VOD_INFO_TTL_MS) {
        await db.delete('vodInfo', composed.key)
        return null
      }
      // Touch lastReadAt — fire-and-forget COM DEBOUNCE.
      // Records v3 órfãos (lastReadAt undefined) sempre touch (precisam
      // do field pra LRU funcionar). Records v4 com touch <1min: skip.
      const now = Date.now()
      const lastRead = record.lastReadAt
      const shouldTouch = typeof lastRead !== 'number' || now - lastRead > TOUCH_DEBOUNCE_MS
      if (shouldTouch) {
        const touched: VodInfoRecord = { ...record, lastReadAt: now }
        void db.put('vodInfo', touched).catch(() => undefined)
      }
      return record
    } catch {
      return null
    }
  },

  /** Grava item enriquecido. No-op se logged out.
   *  Inicializa `lastReadAt = storedAt` (write count como read recente). */
  async set(item: VodItem & { ext?: string | undefined }): Promise<void> {
    const composed = composeKey(item.id)
    if (!composed) return
    try {
      const db = await getDb()
      const now = Date.now()
      const record: VodInfoRecord = {
        key: composed.key,
        providerScope: composed.providerScope,
        profileScope: composed.profileScope,
        vodId: item.id,
        storedAt: now,
        lastReadAt: now,
        schemaVersion: VOD_INFO_SCHEMA_VERSION,
        item,
      }
      await db.put('vodInfo', record)
    } catch {
      // sem disco — segue sem cache
    }
  },

  /** Limpa cache de um provider específico (TODOS perfis dele).
   *  Reservado pra "Limpar dados deste parceiro" (Settings — não usado
   *  ainda; granular vs hardResetLocalDb que limpa tudo). */
  async clearProvider(providerScope: string): Promise<void> {
    try {
      const db = await getDb()
      const tx = db.transaction('vodInfo', 'readwrite')
      const idx = tx.store.index('by-provider')
      let cursor = await idx.openCursor(providerScope)
      while (cursor) {
        await cursor.delete()
        cursor = await cursor.continue()
      }
      await tx.done
    } catch {
      // ignora
    }
  },

  /** Remove items expirados (storedAt + TTL > now) ou de schema antigo.
   *  Roda em background pelo long-session-cleaner — items nunca relidos
   *  ficavam pra sempre porque TTL só é checado em `get`. Retorna
   *  quantos items removidos (telemetria).
   *
   *  2-pass (M3 do code review): coleta keys numa transação readonly,
   *  depois bulk delete em transação readwrite. cursor.delete() durante
   *  iteração tem comportamento não-determinístico em IDB Tizen 2017
   *  (premissa do projeto). 2-pass é portável.
   *
   *  Cap MAX_PRUNE_PER_RUN = 1000 evita lock prolongado em store gigante
   *  (10k+ items expirados acumulados). Próxima run pega o resto. */
  async pruneExpired(): Promise<number> {
    const MAX_PRUNE_PER_RUN = 1000
    try {
      const db = await getDb()
      const now = Date.now()
      // Pass 1 — collect (readonly).
      const expiredKeys: string[] = []
      let cursor = await db.transaction('vodInfo', 'readonly').store.openCursor()
      while (cursor && expiredKeys.length < MAX_PRUNE_PER_RUN) {
        const r = cursor.value
        if (
          r.schemaVersion !== VOD_INFO_SCHEMA_VERSION ||
          now - r.storedAt > VOD_INFO_TTL_MS
        ) {
          expiredKeys.push(r.key)
        }
        cursor = await cursor.continue()
      }
      if (expiredKeys.length === 0) return 0
      // Pass 2 — bulk delete (readwrite).
      const tx = db.transaction('vodInfo', 'readwrite')
      await Promise.all(expiredKeys.map((k) => tx.store.delete(k)))
      await tx.done
      return expiredKeys.length
    } catch {
      return 0
    }
  },

  /** Conta items no store. Usado pelo idb-quota-manager pra decidir
   *  política de eviction. Não retorna bytes (IDB não expõe size por
   *  store) — caller usa heurística (count × tamanho médio estimado). */
  async count(): Promise<number> {
    try {
      const db = await getDb()
      return await db.count('vodInfo')
    } catch {
      return 0
    }
  },

  /** Evict LRU: remove até `targetRemoval` items mais antigos por
   *  `lastReadAt`. Usado pelo idb-quota-manager quando IDB passa do
   *  threshold crítico (90% da quota).
   *
   *  Records v3 órfãos (lastReadAt undefined) ficam fora do índice
   *  by-last-read; fallback faz scan ASC por storedAt no MESMO
   *  cursor read (M3 do code review — antes eram 2 transações
   *  separadas, em vlow Tizen 2017 com 5k records causava frame drop
   *  no player rodando junto).
   *
   *  Retorna quantos items removidos (telemetria). */
  async evictLru(targetRemoval: number): Promise<number> {
    if (targetRemoval <= 0) return 0
    try {
      const db = await getDb()
      const keysFromIndex: string[] = []
      const orphans: { key: string; storedAt: number }[] = []

      // 1 transação readonly que faz: cursor sobre índice by-last-read
      // (records com lastReadAt válido) + cursor sobre store (pra
      // identificar órfãos v3 sem lastReadAt). Ambos no mesmo `tx`.
      const readTx = db.transaction('vodInfo', 'readonly')

      // Primeiro o índice — ordem ASC por lastReadAt.
      let idxCursor = await readTx.store.index('by-last-read').openCursor()
      while (idxCursor && keysFromIndex.length < targetRemoval) {
        keysFromIndex.push(idxCursor.value.key)
        idxCursor = await idxCursor.continue()
      }

      // Se ainda precisa de mais, scan store pra órfãos.
      const remaining = targetRemoval - keysFromIndex.length
      if (remaining > 0) {
        let scanCursor = await readTx.store.openCursor()
        while (scanCursor) {
          const r = scanCursor.value
          if (typeof r.lastReadAt !== 'number') {
            orphans.push({ key: r.key, storedAt: r.storedAt })
          }
          scanCursor = await scanCursor.continue()
        }
      }
      await readTx.done

      // Compõe lista final: índice + N órfãos mais antigos por storedAt.
      const keysToEvict = [...keysFromIndex]
      if (remaining > 0 && orphans.length > 0) {
        orphans.sort((a, b) => a.storedAt - b.storedAt)
        for (const o of orphans.slice(0, remaining)) {
          keysToEvict.push(o.key)
        }
      }
      if (keysToEvict.length === 0) return 0

      // Pass final — bulk delete (readwrite, transação separada).
      const writeTx = db.transaction('vodInfo', 'readwrite')
      await Promise.all(keysToEvict.map((k) => writeTx.store.delete(k)))
      await writeTx.done
      return keysToEvict.length
    } catch {
      return 0
    }
  },

  /** Limpa cache de um perfil específico (ex: deletar perfil).
   *  Reservado — ainda não há caller; futura tela de gestão de perfis. */
  async clearProfile(profileScope: string): Promise<void> {
    try {
      const db = await getDb()
      const tx = db.transaction('vodInfo', 'readwrite')
      const idx = tx.store.index('by-profile')
      let cursor = await idx.openCursor(profileScope)
      while (cursor) {
        await cursor.delete()
        cursor = await cursor.continue()
      }
      await tx.done
    } catch {
      // ignora
    }
  },
}
